import { useMemo } from 'react';

import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

import { EditProfile } from './edit-profile.component';
import { useEditProfileController } from './use-edit-profile-controller.hook';
import { getProfileFormValidationSchema } from './edit-profile.schema';
import { useHandleUpdateProfileError } from './edit-profile.utils';

export type EditProfileControllerProps = {
  onCancelClick(): void;
  onProfileUpdated(params: { isProfileCompletionTriggered?: boolean }): void;
};

export const EditProfileController = ({ onCancelClick, onProfileUpdated }: EditProfileControllerProps) => {
  const {
    config: {
      loyaltyProgram: {
        membershipMinAge,
        membershipMaxAge,
        isExplicitTermsAcceptanceRequired,
        isExplicitSmsPromotionAcceptanceEnabled,
      },
      termsConditions: { url: termsConditionsLink },
      privacyPolicy: { url: privacyPolicyLink },
    },
  } = useConfiguration();
  const handleError = useHandleUpdateProfileError();
  const { loading, updateCustomer, initialValues, membership, isExplicitTermsAcceptanceEnabled } =
    useEditProfileController({
      onComplete: onProfileUpdated,
      onError: handleError,
      isExplicitTermsAcceptanceRequired,
      isExplicitSmsPromotionAcceptanceEnabled,
    });

  const { validationSchema, maxPossibleYOB, minPossibleYOB } = useMemo(
    () =>
      getProfileFormValidationSchema({
        membershipMinAge,
        membershipMaxAge,
        isExplicitTermsAcceptanceRequired,
      }),
    [isExplicitTermsAcceptanceRequired, membershipMaxAge, membershipMinAge],
  );

  return (
    <EditProfile
      loading={loading}
      initialValues={initialValues}
      updateCustomer={updateCustomer}
      isProfileCompleted={membership.isCompleted}
      onCloseClick={onCancelClick}
      validationSchema={validationSchema}
      maxPossibleYOB={maxPossibleYOB}
      minPossibleYOB={minPossibleYOB}
      termsConditionsLink={termsConditionsLink}
      privacyPolicyLink={privacyPolicyLink}
      isExplicitTermsAcceptanceEnabled={isExplicitTermsAcceptanceEnabled}
      isExplicitSmsPromotionAcceptanceEnabled={isExplicitSmsPromotionAcceptanceEnabled}
    />
  );
};
